/**
 * The Pairing mark: the owner's two-pear logo, served as public/brand/logo[-light]-{64,128,256,512}.png by
 * `pnpm brand:icons`. The component is an <img> with width and height set, so nothing shifts on load, and a srcset so
 * 2x and 3x screens get a sharp copy.
 *
 * There are two masters (apps/portal/brand/src/, both transparent): logo.png has white stems and is for navy grounds;
 * logo-light.png has navy stems, since white ones vanish on white. `variant` names the ground the mark sits on:
 * 'dark' and 'reversed' get the white stems, everything else the navy ones. Files that cannot use this component
 * (favicon, app icons, OG images) come from the masters through scripts/brand-icons.ts.
 */

export type MarkVariant = 'light' | 'dark' | 'reversed' | 'mono';

const WIDTHS = [64, 128, 256, 512] as const;
const srcSet = (name: string) => WIDTHS.map((w) => `/brand/${name}-${w}.png ${w}w`).join(', ');
const ON_NAVY = { src: '/brand/logo-128.png', srcSet: srcSet('logo') };
const ON_LIGHT = { src: '/brand/logo-light-128.png', srcSet: srcSet('logo-light') };

export interface PairingMarkProps {
  /** The ground the mark sits on: 'dark' or 'reversed' for navy, otherwise light. */
  variant?: MarkVariant;
  /** Rendered height in px; the mark is square. */
  height?: number;
  /** Set when the mark sits beside the wordmark text, which already names it. */
  decorative?: boolean;
  /** Alt text in place of "Pairing mark", for where the mark stands in for something else. */
  alt?: string;
  className?: string;
}

export function PairingMark({ variant = 'light', height = 32, decorative = false, alt = 'Pairing mark', className }: PairingMarkProps) {
  const art = variant === 'dark' || variant === 'reversed' ? ON_NAVY : ON_LIGHT;
  return (
    <img
      className={className}
      src={art.src}
      srcSet={art.srcSet}
      sizes={`${height}px`}
      width={height}
      height={height}
      alt={decorative ? '' : alt}
      aria-hidden={decorative ? true : undefined}
      decoding="async"
      draggable={false}
    />
  );
}
